import { Check, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/cn';
import { useCachedAccess } from '@/lib/access/use-access';
import { isAllOfBound, scopeSummary, useAirportScope } from '@/lib/operator-context/airport-scope';
import { useAirportsTree } from '@/lib/operator-context/use-airports-tree';
import {
  boundTree,
  countryAirports,
  regionAirports,
  type AirportsTree,
} from '@/lib/operator-context/types';

// Header airport/region scope picker (NEX-310 / operator-context S5). Sits in
// the top bar next to the ⌘K search; the selection narrows the Cases +
// Disruptions lists (the NEX-308 `airports=` wire). Options come from the
// tenant airports-tree; picking a region/country REPLACES the selection with
// all its airports (the tree node is expanded client-side — domains only ever
// see concrete IATA sets), picking an airport toggles it, "All airports"
// clears. Friendly labels primary (§18); codes ride secondary.
//
// Bounded by the operator's scope (users-roles-permissions R5, brief §4.18): only the
// airports in `access.airports` are offered, and "All airports" becomes "My airports
// (N)" — all of MINE, never the airline's. `'*'` keeps the tree as it was. The bound
// replaces the old one-time seeding from the scope claim (NEX-312): an operator's
// default field of vision now simply IS their scope. No airports (or no access) → no
// picker; access still loading → a disabled trigger, never the unbounded tree.

/** True when every one of the node's airports is in the selection. */
function allSelected(nodeCodes: string[], selected: ReadonlySet<string>): boolean {
  return nodeCodes.length > 0 && nodeCodes.every((c) => selected.has(c));
}

export function AirportScopePicker() {
  const { t } = useTranslation();
  const access = useCachedAccess();
  const { airports, setAirports } = useAirportScope();
  const bound = access.ready ? access.airports : null;
  const usable = !access.noAccess && bound !== null && (bound === '*' || bound.length > 0);
  const { data: fullTree } = useAirportsTree(usable);

  if (access.loading) {
    return (
      <Button
        variant="outline"
        size="sm"
        className="h-9 gap-2"
        disabled
        data-testid="airport-scope-trigger"
      >
        <MapPin className="h-4 w-4" aria-hidden />
        <span className="text-xs">{t('admin:access.scope.pending')}</span>
      </Button>
    );
  }
  if (!usable || bound === null) return null;

  const tree = fullTree ? boundTree(fullTree, bound) : undefined;
  const bounded = bound !== '*';
  const allMine = isAllOfBound(airports, bound);
  // "All airports" for the whole airline; "My airports (N)" for a bounded operator.
  const allLabel = !bounded
    ? t('topbar.allAirports')
    : bound.length === 1
      ? bound[0]!
      : t('admin:access.scope.mine', { count: bound.length });
  const summary = allMine ? allLabel : scopeSummary(airports, allLabel);

  const selected = new Set(airports);
  const toggleAirport = (code: string): void => {
    const next = new Set(selected);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setAirports([...next]);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('h-9 gap-2', !allMine && 'border-primary/50 text-primary')}
          aria-label={t('topbar.airportScope')}
          data-testid="airport-scope-trigger"
        >
          <MapPin className="h-4 w-4" aria-hidden />
          <span className="text-xs">{summary}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-96 w-64 overflow-y-auto">
        <DropdownMenuItem
          onSelect={() => setAirports([])}
          data-testid="airport-scope-all"
          className="font-medium"
        >
          <Check
            className={cn('mr-2 h-4 w-4', allMine ? 'opacity-100' : 'opacity-0')}
            aria-hidden
          />
          {allLabel}
        </DropdownMenuItem>
        {tree ? <TreeItems tree={tree} selected={selected} onRegionOrCountry={setAirports} onAirport={toggleAirport} /> : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function TreeItems({
  tree,
  selected,
  onRegionOrCountry,
  onAirport,
}: {
  tree: AirportsTree;
  selected: ReadonlySet<string>;
  /** Region/country click REPLACES the selection with the node's leaf set. */
  onRegionOrCountry: (codes: string[]) => void;
  onAirport: (code: string) => void;
}) {
  return (
    <>
      {tree.regions.map((region) => {
        const regionCodes = regionAirports(region);
        return (
          <div key={region.code}>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => onRegionOrCountry(regionCodes)}
              data-testid={`airport-scope-region-${region.code}`}
              className="font-semibold uppercase text-xs text-muted-foreground"
            >
              <Check
                className={cn('mr-2 h-3 w-3', allSelected(regionCodes, selected) ? 'opacity-100' : 'opacity-0')}
                aria-hidden
              />
              {region.label}
            </DropdownMenuItem>
            {region.countries.map((country) => {
              const codes = countryAirports(country);
              return (
                <div key={country.code}>
                  {/* A country row only earns its own line when the region has several. */}
                  {region.countries.length > 1 ? (
                    <DropdownMenuItem
                      onSelect={() => onRegionOrCountry(codes)}
                      data-testid={`airport-scope-country-${country.code}`}
                      className="pl-4 text-xs text-muted-foreground"
                    >
                      <Check
                        className={cn('mr-2 h-3 w-3', allSelected(codes, selected) ? 'opacity-100' : 'opacity-0')}
                        aria-hidden
                      />
                      {country.label}
                    </DropdownMenuItem>
                  ) : null}
                  {country.airports.map((airport) => (
                    <DropdownMenuItem
                      key={airport.code}
                      onSelect={(e) => {
                        // Keep the menu open while multi-selecting airports.
                        e.preventDefault();
                        onAirport(airport.code);
                      }}
                      data-testid={`airport-scope-airport-${airport.code}`}
                      className="pl-6"
                    >
                      <Check
                        className={cn('mr-2 h-4 w-4', selected.has(airport.code) ? 'opacity-100' : 'opacity-0')}
                        aria-hidden
                      />
                      <span className="truncate">{airport.label}</span>
                    </DropdownMenuItem>
                  ))}
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
}
